import { useState } from "react";
import { Link } from "react-router-dom";
import { Glyph } from "@apf/design-system/glyphs";
import type {
  AuthoringGuideDefinition,
  AuthoringGuideResource,
} from "@apf/framework-config";
import { downloadAssetPath } from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { EmptyState, PageHeader, SectionHeading } from "../components/ui";

type CopyState = "idle" | "copied" | "error";

function replaceNumber(template: string, value: number): string {
  return template.replace("{number}", String(value));
}

function ResourceLink({ resource }: { resource: AuthoringGuideResource }) {
  const copy = useCopy();
  const to =
    resource.kind === "asset"
      ? downloadAssetPath(resource.target)
      : resource.target;
  return (
    <li className="apf-authoring-guide__resource">
      <div>
        <h3>{copy(resource.labelKey)}</h3>
        <p>{copy(resource.descriptionKey)}</p>
      </div>
      <Link className="apf-button apf-button--quiet" to={to}>
        {copy("page.authoring-guide.open-resource")}
      </Link>
    </li>
  );
}

function ResourceChips({
  guide,
  resourceIds,
}: {
  guide: AuthoringGuideDefinition;
  resourceIds?: string[];
}) {
  const copy = useCopy();
  if (!resourceIds?.length) return null;
  const resources = new Map(guide.resources.map((resource) => [resource.id, resource]));
  return (
    <ul className="apf-authoring-guide__resource-chips">
      {resourceIds.map((resourceId) => {
        const resource = resources.get(resourceId);
        if (!resource) return null;
        const to =
          resource.kind === "asset"
            ? downloadAssetPath(resource.target)
            : resource.target;
        return (
          <li key={resource.id}>
            <Link to={to}>{copy(resource.labelKey)}</Link>
          </li>
        );
      })}
    </ul>
  );
}

export function AuthoringGuidePage({ guideId }: { guideId: AuthoringGuideDefinition["id"] }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const guide = snapshot.authoringGuides.guides.find(({ id }) => id === guideId);

  if (!guide) {
    return (
      <div className="apf-page apf-register-reference">
        <EmptyState
          title={copy("absence.address.title")}
          description={copy("absence.address.support")}
        />
      </div>
    );
  }

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(copy(guide.prompt.bodyKey));
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };
  const copyLabel =
    copyState === "copied"
      ? copy("page.authoring-guide.copied-prompt")
      : copyState === "error"
        ? copy("page.authoring-guide.copy-failed")
        : copy("page.authoring-guide.copy-prompt");

  return (
    <div className="apf-page apf-register-reference apf-authoring-guide">
      <PageHeader
        eyebrow={copy(guide.eyebrowKey)}
        title={copy(guide.titleKey)}
        description={copy(guide.descriptionKey)}
        meta={`${copy("page.downloads.version-label")} ${guide.version}`}
      />

      <section className="apf-authoring-guide__outcome" aria-labelledby={`${guide.id}-outcome`}>
        <p className="apf-authoring-guide__label">{copy("page.authoring-guide.outcome-label")}</p>
        <h2 id={`${guide.id}-outcome`}>{copy(guide.outcomeKey)}</h2>
      </section>

      <section className="apf-page-section" aria-labelledby={`${guide.id}-pathways`}>
        <SectionHeading
          id={`${guide.id}-pathways`}
          title={copy("page.authoring-guide.pathways-title")}
        />
        <div className="apf-authoring-guide__pathways">
          {guide.pathways.map((pathway) => (
            <article className="apf-authoring-guide__pathway" key={pathway.id}>
              <h3>{copy(pathway.titleKey)}</h3>
              <p>{copy(pathway.summaryKey)}</p>
              <ol>
                {pathway.steps.map((step, index) => (
                  <li key={step.id}>
                    <p className="apf-authoring-guide__step-label">
                      {replaceNumber(copy("page.authoring-guide.step-label"), index + 1)}
                    </p>
                    <h4>{copy(step.titleKey)}</h4>
                    <p>{copy(step.bodyKey)}</p>
                    <ResourceChips guide={guide} resourceIds={step.resourceIds} />
                  </li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      </section>

      <section className="apf-page-section" aria-labelledby={`${guide.id}-contract`}>
        <SectionHeading
          id={`${guide.id}-contract`}
          title={copy("page.authoring-guide.guide-title")}
        />
        <div className="apf-authoring-guide__sections">
          {guide.sections.map((section) => (
            <section key={section.id} aria-labelledby={`${guide.id}-${section.id}`}>
              <h3 id={`${guide.id}-${section.id}`}>{copy(section.titleKey)}</h3>
              {section.introductionKey ? <p>{copy(section.introductionKey)}</p> : null}
              <div className="apf-authoring-guide__items">
                {section.items.map((item) => (
                  <article key={item.id}>
                    <h4>{copy(item.titleKey)}</h4>
                    <p>{copy(item.bodyKey)}</p>
                    <ResourceChips guide={guide} resourceIds={item.resourceIds} />
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>

      <section className="apf-page-section apf-authoring-guide__checklist" aria-labelledby={`${guide.id}-checklist`}>
        <SectionHeading
          id={`${guide.id}-checklist`}
          title={copy("page.authoring-guide.checklist-title")}
        />
        <ul>
          {guide.checklist.map((item) => (
            <li key={item.id}>
              <Glyph meaning="utility.check" size={16} />
              <span>{copy(item.labelKey)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="apf-page-section" aria-labelledby={`${guide.id}-resources`}>
        <SectionHeading
          id={`${guide.id}-resources`}
          title={copy("page.authoring-guide.resources-title")}
          description={copy("page.authoring-guide.resources-description")}
        />
        <ul className="apf-authoring-guide__resources">
          {guide.resources.map((resource) => (
            <ResourceLink key={resource.id} resource={resource} />
          ))}
        </ul>
      </section>

      <section className="apf-page-section apf-authoring-guide__prompt" aria-labelledby={`${guide.id}-prompt`}>
        <SectionHeading
          id={`${guide.id}-prompt`}
          title={copy(guide.prompt.titleKey)}
          description={copy("page.authoring-guide.prompt-description")}
        />
        <div className="apf-authoring-guide__prompt-actions">
          <button className="apf-button" type="button" onClick={copyPrompt} data-copy-state={copyState}>
            {copyState === "copied" ? <Glyph meaning="utility.check" size={16} /> : null}
            <span aria-live="polite">{copyLabel}</span>
          </button>
          <Link className="apf-button apf-button--quiet" to="/downloads/groundworks-authoring-kit">
            {copy("page.authoring-guide.download-kit")}
          </Link>
        </div>
        <pre tabIndex={0}>
          <code>{copy(guide.prompt.bodyKey)}</code>
        </pre>
      </section>
    </div>
  );
}
